import { useMemo } from 'react';
import type { ApproverUser, Workflow } from '@/components/ApprovalStageWizard/types';
import type { Employee } from '@/types/timesheet';
import type { ApprovalStatus, ApprovalWorkflow } from '../types';
import { MOCK_APPROVER_USERS } from '../utils';

export interface UseWorkflowDerivedStateResult {
  approverUsers: ApproverUser[];
  workflowAssignmentCounts: Record<string, number>;
  approvals: ApprovalWorkflow[];
  filteredApprovals: ApprovalWorkflow[];
}

/**
 * Derives display-ready data (approver users, assignment counts, list rows and
 * the filtered list) from the loaded workflows and employees.
 */
export const useWorkflowDerivedState = (
  workflows: Workflow[],
  employees: Employee[],
  isLive: boolean,
  searchQuery: string,
  statusFilter: string
): UseWorkflowDerivedStateResult => {
  // Map employees to ApproverUser format for the Specific Person dropdown
  const approverUsers: ApproverUser[] = useMemo(() => {
    // In demo mode, create mock approver users from the mock employee data
    if (!isLive) {
      return MOCK_APPROVER_USERS;
    }

    // In live mode, use real employees
    if (employees.length === 0) return [];
    return employees.filter((emp) => emp.type?.toLowerCase() !== 'contractor').map((emp) => ({
      id: String(emp.id),
      name: emp.name,
      role: emp.userRoleName || emp.role || '',
    }));
  }, [isLive, employees]);

  // Compute assigned employee counts per workflow from employees data
  const workflowAssignmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    employees.forEach((emp) => {
      if (emp.approvalWorkflowId) {
        counts[emp.approvalWorkflowId] = (counts[emp.approvalWorkflowId] || 0) + 1;
      }
    });
    return counts;
  }, [employees]);

  // Derive approvals from workflows for display (single source of truth)
  const approvals: ApprovalWorkflow[] = useMemo(() => {
    return workflows.map((workflow) => ({
      id: workflow.id,
      name: workflow.name,
      status: (workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1)) as ApprovalStatus,
      stages: workflow.stages.length,
      assignedEmployees: workflowAssignmentCounts[workflow.id] || 0,
      lastModified: workflow.updatedAt,
      isDefault: workflow.isDefault,
    }));
  }, [workflows, workflowAssignmentCounts]);

  // Filter and search logic
  // Only showing workflows with Active, Inactive, and Draft statuses
  const filteredApprovals = useMemo(() => {
    return approvals.filter((approval) => {
      // Only show allowed statuses (Active, Inactive, Draft)
      const allowedStatuses: ApprovalStatus[] = ['Active', 'Inactive', 'Draft'];
      if (!allowedStatuses.includes(approval.status)) {
        return false;
      }

      // Filter by status
      const matchesStatus = statusFilter === 'all' || approval.status === statusFilter;

      // Filter by search query
      const matchesSearch = approval.name.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesStatus && matchesSearch;
    });
  }, [approvals, statusFilter, searchQuery]);

  return { approverUsers, workflowAssignmentCounts, approvals, filteredApprovals };
};
